import {
  applyPictureChroma,
  hasImagePicture,
  invertPictureMatrix,
  pictureHomography,
  PICTURE_CORNERS,
  tracePictureContours,
  type NormalizedImagePicture,
  type PictureMask,
} from "./image-picture";

type ImageLoader = (
  url: string,
  signal?: AbortSignal,
) => Promise<{ image: HTMLImageElement; revoke: () => void }>;
const surface = (width: number, height: number) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};
function context(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("picture-canvas-unavailable");
  return ctx;
}
function release(canvas: HTMLCanvasElement) {
  canvas.width = 0;
  canvas.height = 0;
}
function rgba(hex: string, alpha: number) {
  return `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)},${alpha})`;
}
function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

async function maskSurface(
  mask: PictureMask,
  width: number,
  height: number,
  load: ImageLoader,
  signal?: AbortSignal,
): Promise<HTMLCanvasElement> {
  const canvas = surface(width, height),
    ctx = context(canvas),
    w = mask.width * width,
    h = mask.height * height;
  try {
    if (mask.shape === "cutout") {
      if (!mask.maskUrl) throw new Error("picture-cutout-missing");
      const loaded = await load(mask.maskUrl, signal);
      try {
        ctx.drawImage(loaded.image, 0, 0, width, height);
        const pixels = ctx.getImageData(0, 0, width, height);
        for (let i = 0; i < pixels.data.length; i += 4) {
          const a =
            ((0.2126 * pixels.data[i] +
              0.7152 * pixels.data[i + 1] +
              0.0722 * pixels.data[i + 2]) *
              pixels.data[i + 3]) /
            255;
          pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = 255;
          pixels.data[i + 3] = a;
        }
        const raster = surface(width, height);
        try {
          context(raster).putImageData(pixels, 0, 0);
          ctx.clearRect(0, 0, width, height);
          ctx.save();
          ctx.translate(mask.x * width, mask.y * height);
          ctx.rotate((mask.rotation * Math.PI) / 180);
          ctx.drawImage(raster, -w / 2, -h / 2, w, h);
          ctx.restore();
        } finally {
          release(raster);
        }
      } finally {
        loaded.revoke();
      }
    } else {
      ctx.save();
      ctx.translate(mask.x * width, mask.y * height);
      ctx.rotate((mask.rotation * Math.PI) / 180);
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#ffffff";
      if (mask.shape === "ellipse") {
        ctx.beginPath();
        ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (mask.shape === "rectangle") ctx.fillRect(-w / 2, -h / 2, w, h);
      else if (mask.shape === "linear") {
        const span = Math.max(1, h * 0.5),
          g = ctx.createLinearGradient(0, -span / 2, 0, span / 2);
        g.addColorStop(0, "rgba(255,255,255,0)");
        g.addColorStop(1, "#fff");
        ctx.fillStyle = g;
        ctx.fillRect(-width * 3, -height * 3, width * 6, height * 6);
      } else if (mask.shape === "mirror") {
        const g = ctx.createLinearGradient(0, -h / 2, 0, h / 2);
        g.addColorStop(0, "rgba(255,255,255,0)");
        g.addColorStop(0.25, "#fff");
        g.addColorStop(0.75, "#fff");
        g.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = g;
        ctx.fillRect(-width * 3, -h / 2, width * 6, h);
      } else if (mask.shape === "polygon" || mask.shape === "star") {
        if (mask.points.length >= 3) {
          ctx.beginPath();
          ctx.moveTo(
            (mask.points[0].x - 0.5) * w,
            (mask.points[0].y - 0.5) * h,
          );
          for (let i = 1; i <= mask.points.length; i++) {
            const prev = mask.points[i - 1],
              point = mask.points[i % mask.points.length];
            if (prev.out || point.in) {
              const c = prev.out ?? prev,
                d = point.in ?? point;
              ctx.bezierCurveTo(
                (c.x - 0.5) * w,
                (c.y - 0.5) * h,
                (d.x - 0.5) * w,
                (d.y - 0.5) * h,
                (point.x - 0.5) * w,
                (point.y - 0.5) * h,
              );
            } else ctx.lineTo((point.x - 0.5) * w, (point.y - 0.5) * h);
          }
          ctx.closePath();
          ctx.fill();
        }
      } else if (mask.shape === "brush") {
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (const stroke of mask.strokes) {
          if (!stroke.points.length) continue;
          ctx.globalCompositeOperation = stroke.erase
            ? "destination-out"
            : "source-over";
          ctx.lineWidth = stroke.size * Math.min(width, height);
          ctx.beginPath();
          stroke.points.forEach((p, i) => {
            const x = (p.x - 0.5) * w,
              y = (p.y - 0.5) * h;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          if (stroke.points.length === 1) {
            const p = stroke.points[0];
            ctx.arc(
              (p.x - 0.5) * w,
              (p.y - 0.5) * h,
              ctx.lineWidth / 2,
              0,
              Math.PI * 2,
            );
            ctx.fill();
          } else ctx.stroke();
        }
      } else if (mask.shape === "text" && mask.text) {
        const lines = mask.text.split("\n"),
          size = Math.max(1, (h / Math.max(1, lines.length)) * 0.8),
          font = `700 ${Math.round(size)}px ${mask.font}`;
        await document.fonts.load(font, mask.text);
        signal?.throwIfAborted();
        ctx.font = font;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        lines.forEach((line, i) =>
          ctx.fillText(line, 0, (i - (lines.length - 1) / 2) * size * 1.2, w),
        );
      }
      ctx.restore();
    }
    if (mask.feather > 0) {
      const blurred = surface(width, height);
      try {
        const b = context(blurred);
        b.filter = `blur(${mask.feather * Math.min(width, height) * 0.25}px)`;
        b.drawImage(canvas, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(blurred, 0, 0);
      } finally {
        release(blurred);
      }
    }
    if (mask.invert) {
      const inverse = surface(width, height);
      try {
        const i = context(inverse);
        i.fillStyle = "#fff";
        i.fillRect(0, 0, width, height);
        i.globalCompositeOperation = "destination-out";
        i.drawImage(canvas, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(inverse, 0, 0);
      } finally {
        release(inverse);
      }
    }
    return canvas;
  } catch (error) {
    release(canvas);
    throw error;
  }
}

/** 使用真正的投影矩阵采样纹理，避免把透视变形拆成有接缝的仿射三角网格。 */
let projectionCanvas: HTMLCanvasElement | null = null;
function projectPicture(
  source: HTMLCanvasElement,
  corners: NormalizedImagePicture["perspective"],
): HTMLCanvasElement {
  const matrix = pictureHomography(corners),
    inverse = invertPictureMatrix(matrix),
    canvas = projectionCanvas ?? surface(source.width, source.height);
  projectionCanvas = canvas;
  if (canvas.width !== source.width) canvas.width = source.width;
  if (canvas.height !== source.height) canvas.height = source.height;
  const gl = canvas.getContext("webgl", {
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
    antialias: false,
  });
  if (!gl) {
    release(canvas);
    throw new Error("picture-webgl-unavailable");
  }
  const shaders: WebGLShader[] = [],
    program = gl.createProgram(),
    buffer = gl.createBuffer(),
    texture = gl.createTexture();
  try {
    if (!program || !buffer || !texture)
      throw new Error("picture-webgl-unavailable");
    const compile = (type: number, code: string) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error("picture-webgl-unavailable");
      shaders.push(shader);
      gl.shaderSource(shader, code);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
        throw new Error("picture-shader-failed");
      gl.attachShader(program, shader);
    };
    compile(
      gl.VERTEX_SHADER,
      "attribute vec2 a_position;void main(){gl_Position=vec4(a_position,0.0,1.0);}",
    );
    compile(
      gl.FRAGMENT_SHADER,
      "precision highp float;uniform sampler2D u_image;uniform mat3 u_inverse;uniform vec2 u_size;void main(){vec2 p=vec2(gl_FragCoord.x/u_size.x,1.0-gl_FragCoord.y/u_size.y);vec3 q=u_inverse*vec3(p,1.0);vec2 uv=q.xy/q.z;if(uv.x<0.0||uv.y<0.0||uv.x>1.0||uv.y>1.0){gl_FragColor=vec4(0.0);}else{gl_FragColor=texture2D(u_image,uv);}}",
    );
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
      throw new Error("picture-shader-failed");
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const location = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.uniform1i(gl.getUniformLocation(program, "u_image"), 0);
    gl.uniform2f(
      gl.getUniformLocation(program, "u_size"),
      canvas.width,
      canvas.height,
    );
    gl.uniformMatrix3fv(
      gl.getUniformLocation(program, "u_inverse"),
      false,
      new Float32Array([
        inverse[0],
        inverse[3],
        inverse[6],
        inverse[1],
        inverse[4],
        inverse[7],
        inverse[2],
        inverse[5],
        inverse[8],
      ]),
    );
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    if (gl.getError() !== gl.NO_ERROR)
      throw new Error("picture-perspective-failed");
    return canvas;
  } catch (error) {
    release(canvas);
    throw error;
  } finally {
    for (const shader of shaders) gl.deleteShader(shader);
    if (texture) gl.deleteTexture(texture);
    if (buffer) gl.deleteBuffer(buffer);
    if (program) gl.deleteProgram(program);
  }
}

/** 画面裁切和局部调色共用蒙版组合规则，透明像素不会改变底图。调用方负责释放。 */
export async function renderPictureMaskStack(
  masks: PictureMask[],
  width: number,
  height: number,
  load: ImageLoader,
  signal?: AbortSignal,
): Promise<HTMLCanvasElement> {
  const combined = surface(width, height),
    c = context(combined);
  try {
    const enabled = masks.filter((mask) => mask.enabled);
    if (
      !enabled.length ||
      ["subtract", "intersect"].includes(enabled[0].combine)
    ) {
      c.fillStyle = "#fff";
      c.fillRect(0, 0, width, height);
    }
    for (const mask of enabled) {
      signal?.throwIfAborted();
      const shape = await maskSurface(mask, width, height, load, signal);
      try {
        if (mask.combine === "replace") {
          c.clearRect(0, 0, width, height);
          c.globalCompositeOperation = "source-over";
        } else
          c.globalCompositeOperation =
            mask.combine === "subtract"
              ? "destination-out"
              : mask.combine === "intersect"
                ? "destination-in"
                : "source-over";
        c.drawImage(shape, 0, 0);
      } finally {
        release(shape);
      }
    }
    return combined;
  } catch (error) {
    release(combined);
    throw error;
  }
}

export async function blendPictureAdjustmentMasks(
  original: ImageData,
  adjusted: ImageData,
  masks: PictureMask[],
  load: ImageLoader,
  signal?: AbortSignal,
): Promise<void> {
  if (!masks.some((mask) => mask.enabled)) return;
  const mask = await renderPictureMaskStack(
    masks,
    original.width,
    original.height,
    load,
    signal,
  );
  try {
    signal?.throwIfAborted();
    const coverage = context(mask).getImageData(
      0,
      0,
      mask.width,
      mask.height,
    ).data;
    for (let i = 0; i < adjusted.data.length; i += 4) {
      const strength = coverage[i + 3] / 255;
      for (let c = 0; c < 3; c++)
        adjusted.data[i + c] =
          original.data[i + c] +
          (adjusted.data[i + c] - original.data[i + c]) * strength;
      adjusted.data[i + 3] = original.data[i + 3];
    }
  } finally {
    release(mask);
  }
}

function drawPictureStroke(
  target: CanvasRenderingContext2D,
  layer: HTMLCanvasElement,
  p: NormalizedImagePicture,
) {
  if (!p.stroke.enabled || p.stroke.width === 0 || p.stroke.opacity === 0)
    return;
  const scale = Math.min(1, 2048 / Math.max(layer.width, layer.height)),
    small = surface(
      Math.max(1, Math.round(layer.width * scale)),
      Math.max(1, Math.round(layer.height * scale)),
    );
  try {
    const ctx = context(small);
    ctx.drawImage(layer, 0, 0, small.width, small.height);
    const paths = tracePictureContours(
      ctx.getImageData(0, 0, small.width, small.height).data,
      small.width,
      small.height,
    );
    const w = layer.width,
      h = layer.height,
      weight = p.stroke.width * Math.min(w, h);
    target.save();
    target.strokeStyle = rgba(p.stroke.color, p.stroke.opacity);
    target.lineWidth = weight * 2;
    target.lineJoin = "round";
    target.lineCap = "round";
    if (p.stroke.style === "dashed")
      target.setLineDash([weight * 4, weight * 3]);
    if (p.stroke.style === "glow") {
      target.shadowColor = rgba(p.stroke.color, p.stroke.opacity);
      target.shadowBlur = weight * 2;
    }
    for (const path of paths) {
      target.beginPath();
      path.forEach((point, i) => {
        const jitter =
            p.stroke.style === "hand"
              ? Math.sin(i * 12.9898) * weight * 0.15
              : 0,
          x = point.x * w + jitter,
          y = point.y * h + jitter;
        if (i === 0) target.moveTo(x, y);
        else target.lineTo(x, y);
      });
      target.closePath();
      target.stroke();
    }
    target.restore();
  } finally {
    release(small);
  }
}

export async function applyImagePictureToCanvas(
  canvas: HTMLCanvasElement,
  p: NormalizedImagePicture,
  load: ImageLoader,
  signal?: AbortSignal,
  processChroma?: (pixels: ImageData, chroma: NormalizedImagePicture['chroma'], signal?: AbortSignal) => Promise<ImageData>,
): Promise<void> {
  if (!hasImagePicture(p)) return;
  signal?.throwIfAborted();
  const { width, height } = canvas,
    foreground = surface(width, height),
    output = surface(width, height);
  let projected: HTMLCanvasElement | null = null;
  try {
    const f = context(foreground),
      out = context(output);
    f.drawImage(canvas, 0, 0);
    if (p.chroma.enabled) {
      const pixels = f.getImageData(0, 0, width, height);
      const keyed = processChroma ? await processChroma(pixels, p.chroma, signal) : pixels;
      if (!processChroma) applyPictureChroma(keyed.data, p.chroma);
      signal?.throwIfAborted();
      f.putImageData(keyed, 0, 0);
    }
    if (p.radius > 0) {
      f.globalCompositeOperation = "destination-in";
      roundedPath(f, 0, 0, width, height, p.radius * Math.min(width, height));
      f.fillStyle = "#fff";
      f.fill();
      f.globalCompositeOperation = "source-over";
    }
    const masks = p.masks.filter((mask) => mask.enabled);
    if (masks.length) {
      const combined = await renderPictureMaskStack(
        masks,
        width,
        height,
        load,
        signal,
      );
      try {
        f.globalCompositeOperation = "destination-in";
        f.drawImage(combined, 0, 0);
        f.globalCompositeOperation = "source-over";
      } finally {
        release(combined);
      }
    }

    if (p.background.mode === "color") {
      out.fillStyle = p.background.color;
      out.fillRect(0, 0, width, height);
    } else if (p.background.mode === "blur") {
      out.save();
      out.filter = `blur(${p.background.blur * Math.min(width, height)}px)`;
      out.drawImage(
        canvas,
        -width * 0.1,
        -height * 0.1,
        width * 1.2,
        height * 1.2,
      );
      out.restore();
    } else if (p.background.mode === "image") {
      if (!p.background.imageUrl) throw new Error("picture-background-missing");
      const image = await load(p.background.imageUrl, signal);
      try {
        const scale = Math.max(
            width / image.image.naturalWidth,
            height / image.image.naturalHeight,
          ),
          w = image.image.naturalWidth * scale,
          h = image.image.naturalHeight * scale;
        out.drawImage(image.image, (width - w) / 2, (height - h) / 2, w, h);
      } finally {
        image.revoke();
      }
    }
    signal?.throwIfAborted();
    const warp = p.perspective.some(
      (point, i) =>
        point.x !== PICTURE_CORNERS[i].x || point.y !== PICTURE_CORNERS[i].y,
    );
    if (warp) projected = projectPicture(foreground, p.perspective);
    const layer = projected ?? foreground;
    // 在输出坐标中组合主体、描边和阴影，再统一透明度/混合。大模糊不会分配数倍边长的 8K 画布。
    const styled = surface(width, height),
      s = context(styled);
    try {
      s.translate(p.x * width, p.y * height);
      s.rotate((p.rotation * Math.PI) / 180);
      s.scale(p.scaleX, p.scaleY);
      s.translate(-width / 2, -height / 2);
      if (p.shadow.enabled) {
        const unit = Math.min(width, height),
          silhouette = surface(width, height),
          sc = context(silhouette);
        try {
          sc.drawImage(layer, 0, 0);
          sc.globalCompositeOperation = "source-in";
          sc.fillStyle = rgba(p.shadow.color, p.shadow.opacity);
          sc.fillRect(0, 0, width, height);
          const angle = (p.shadow.angle * Math.PI) / 180;
          s.save();
          s.filter = `blur(${p.shadow.blur * unit * Math.sqrt(p.scaleX * p.scaleY)}px)`;
          s.drawImage(
            silhouette,
            Math.cos(angle) * p.shadow.distance * unit,
            Math.sin(angle) * p.shadow.distance * unit,
          );
          s.restore();
        } finally {
          release(silhouette);
        }
      }
      drawPictureStroke(s, layer, p);
      s.drawImage(layer, 0, 0);
      out.globalAlpha = p.opacity;
      out.globalCompositeOperation = p.blend;
      out.drawImage(styled, 0, 0);
    } finally {
      release(styled);
    }

    const target = context(canvas);
    target.save();
    try {
      target.setTransform(1, 0, 0, 1, 0, 0);
      target.globalAlpha = 1;
      target.globalCompositeOperation = "copy";
      target.filter = "none";
      target.shadowColor = "rgba(0,0,0,0)";
      target.shadowBlur = target.shadowOffsetX = target.shadowOffsetY = 0;
      target.drawImage(output, 0, 0);
    } finally {
      target.restore();
    }
  } finally {
    if (projected) release(projected);
    release(foreground);
    release(output);
  }
}
